(() => {
  const config = window.SC_WORKBENCH || {};
  const CARD_SELECTOR = '[data-sc-workbench-card]';
  const BUTTON_SELECTOR = '[data-sc-workbench-button]';
  const STATUS_SELECTOR = '[data-sc-workbench-status]';
  const ACCOUNT_SELECTOR = '[data-sc-workbench-account]';

  if (!config.enabled || !config.proxyBase) return;

  let stateCache = null;
  let bootstrapPromise = null;
  let localActiveListId = null;
  let stateIndex = { byItemKey: new Map(), byCardKey: new Map() };
  let moveOverlayNode = null;
  let toastRegionNode = null;
  let lastObservedCartSignature = null;
  let observedCartObserverTimer = null;
  let observedCartObserverInFlight = false;
  let observedCartObserverPending = false;
  let suppressNextCartMutationObserve = false;

  const uiState = {
    loading: false,
    bootstrapError: false,
    query: '',
    sort: 'recent',
    selections: Object.create(null),
    moveContext: null,
    cartSubmitting: false,
  };

  const fallbackState = () => ({
    loggedIn: Boolean(config.loggedIn),
    authorized: Boolean(config.authorized),
    activeListId: 'reorder',
    lists: normalizeLists(Array.isArray(config.listOptions) ? config.listOptions : []),
    itemsByList: {},
    listSummaries: [],
    meta: {
      revision: 0,
      lastMutationAt: null,
      pulse: {
        totalItems: 0,
        reorderReady: 0,
        quoteReady: 0,
        needsAttention: 0,
      },
    },
  });

  function clean(value) {
    return value == null ? '' : String(value).trim();
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  function money(cents) {
    try {
      return Shopify.formatMoney(Number(cents || 0), config.moneyFormat);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  }

  function q(root, selector) {
    return (root || document).querySelector(selector);
  }

  function qa(root, selector) {
    return Array.from((root || document).querySelectorAll(selector));
  }

  function role(root, name) {
    return q(root, `[data-wb-role="${name}"]`);
  }

  function roles(root, name) {
    return qa(root, `[data-wb-role="${name}"]`);
  }

  function setTextAll(root, selector, value) {
    qa(root, selector).forEach((node) => {
      node.textContent = value;
    });
  }

  function setHidden(node, hidden) {
    if (!node) return;
    node.hidden = Boolean(hidden);
    node.classList.toggle('is-hidden', Boolean(hidden));
  }

  function setDisabledAll(root, selector, disabled) {
    qa(root, selector).forEach((node) => {
      node.disabled = Boolean(disabled);
    });
  }

  function setButtonLoadingState(selector, loading, loadingText, root = document) {
    qa(root, selector).forEach((node) => {
      if (!node.dataset.scWorkbenchLabel) {
        node.dataset.scWorkbenchLabel = clean(node.textContent) || clean(node.getAttribute('aria-label')) || '';
      }
      node.classList.toggle('is-loading', Boolean(loading));
      node.setAttribute('aria-busy', loading ? 'true' : 'false');
      node.textContent = loading ? loadingText : node.dataset.scWorkbenchLabel;
    });
  }

  function syncCartBusyUi(root = document) {
    setButtonLoadingState('[data-sc-workbench-add-active-list], [data-wb-action="add-active-to-cart"]', uiState.cartSubmitting, 'Adding…', root);
    setButtonLoadingState('[data-wb-action="add-selected-to-cart"]', uiState.cartSubmitting, 'Adding…', root);
  }

  function buttonLabel(button, key, fallback = '') {
    return clean(button?.dataset?.[key]) || fallback;
  }

  function setButtonLabel(button, label) {
    if (!button) return;
    const labelNode = q(button, '[data-sc-workbench-button-label]');
    if (labelNode) {
      labelNode.textContent = label;
    } else {
      button.textContent = label;
    }
  }

  function statusLabel(status, key, fallback = '') {
    return clean(status?.dataset?.[key]) || fallback;
  }

  function getMoveOverlayNode() {
    if (moveOverlayNode && document.body.contains(moveOverlayNode)) return moveOverlayNode;
    moveOverlayNode = document.querySelector('[data-wb-role="overlay-layer"]');
    if (!moveOverlayNode) return null;
    moveOverlayNode.classList.add('sc-workbench-overlay-portal');
    if (moveOverlayNode.parentNode !== document.body) {
      document.body.appendChild(moveOverlayNode);
    }
    return moveOverlayNode;
  }

  function focusMoveSurface() {
    const overlay = getMoveOverlayNode();
    const target = overlay?.querySelector('[data-wb-action="move-to-destination"]:not([disabled])') ||
      overlay?.querySelector('[data-wb-action="close-move"]');
    target?.focus({ preventScroll: true });
  }


  function ensureToastRegion() {
    if (toastRegionNode && document.body.contains(toastRegionNode)) return toastRegionNode;
    toastRegionNode = document.querySelector('[data-sc-workbench-toast-region]');
    if (!toastRegionNode) {
      toastRegionNode = document.createElement('div');
      toastRegionNode.className = 'sc-workbench-toast-region';
      toastRegionNode.dataset.scWorkbenchToastRegion = 'true';
      toastRegionNode.setAttribute('aria-live', 'polite');
      toastRegionNode.setAttribute('aria-atomic', 'true');
      document.body.appendChild(toastRegionNode);
    }
    return toastRegionNode;
  }

  function showToast(message, variant = 'info') {
    const text = clean(message);
    if (!text) return;
    const region = ensureToastRegion();
    if (!region) return;

    const toast = document.createElement('div');
    toast.className = `sc-workbench-toast sc-workbench-toast--${variant}`;
    toast.setAttribute('role', variant === 'error' ? 'alert' : 'status');
    toast.textContent = text;
    region.appendChild(toast);

    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    const dismiss = () => {
      toast.classList.remove('is-visible');
      window.setTimeout(() => toast.remove(), 220);
    };

    window.setTimeout(dismiss, variant === 'error' ? 4200 : 2600);
  }

  function getRoot() {
    return q(document, ACCOUNT_SELECTOR);
  }

  const SYSTEM_LIST_LABELS = {
    reorder: 'Buy Again',
    standards: 'Go-To Products',
    service: 'Saved for Later',
  };

  function inferListDetails(list) {
    const id = clean(list?.id);
    if (id === 'reorder') {
      return { id, type: 'reorder', name: SYSTEM_LIST_LABELS.reorder, system: true, sortOrder: 10 };
    }
    if (id === 'standards') {
      return { id, type: 'standard', name: SYSTEM_LIST_LABELS.standards, system: true, sortOrder: 20 };
    }
    if (id === 'service') {
      return { id, type: 'service', name: SYSTEM_LIST_LABELS.service, system: true, sortOrder: 30 };
    }
    return {
      id,
      type: clean(list?.type) || (id.startsWith('project-') ? 'project' : 'project'),
      name: clean(list?.name) || 'Project list',
      system: Boolean(list?.system),
      sortOrder: Number.isFinite(Number(list?.sortOrder)) ? Number(list.sortOrder) : 100,
    };
  }

  function normalizeLists(input) {
    return (input || [])
      .map((list) => {
        const merged = { ...inferListDetails(list), ...list };
        // Lock system list display names to canonical labels keyed off stable IDs,
        // preventing old app-proxy stored names from overriding the visible labels.
        if (Object.prototype.hasOwnProperty.call(SYSTEM_LIST_LABELS, merged.id)) {
          merged.name = SYSTEM_LIST_LABELS[merged.id];
        }
        return merged;
      })
      .filter((list) => clean(list.id))
      .filter((list) => list.id !== 'projects')
      .sort((a, b) => Number(a.sortOrder || 999) - Number(b.sortOrder || 999));
  }

  function normalizePayload(payload) {
    if (!payload || typeof payload !== 'object') return fallbackState();

    const state = payload.state || payload;
    const base = fallbackState();
    const lists = normalizeLists(
      Array.isArray(payload.lists) && payload.lists.length
        ? payload.lists
        : Array.isArray(state.lists) && state.lists.length
          ? state.lists
          : base.lists
    );
    const requestedActiveListId = clean(payload.activeListId || state.activeListId || 'reorder');
    const activeListId = lists.some((list) => list.id === requestedActiveListId) ? requestedActiveListId : 'reorder';

    return {
      ...base,
      ...payload,
      state,
      activeListId,
      lists,
      itemsByList: payload.itemsByList || state.itemsByList || {},
      listSummaries: Array.isArray(payload.listSummaries) ? payload.listSummaries : [],
      meta: payload.meta || state.meta || base.meta,
      loggedIn: typeof payload.loggedIn === 'boolean' ? payload.loggedIn : Boolean(config.loggedIn),
      authorized: typeof payload.authorized === 'boolean' ? payload.authorized : Boolean(config.authorized),
    };
  }

  function itemCardKeys(obj) {
    return [clean(obj?.variantId), clean(obj?.productId), clean(obj?.handle), clean(obj?.sku)].filter(Boolean);
  }

  function rebuildStateIndex(state) {
    const byItemKey = new Map();
    const byCardKey = new Map();

    for (const [listId, items] of Object.entries(state?.itemsByList || {})) {
      for (const entry of items || []) {
        const itemKey = itemKeyLike(entry);
        if (itemKey && !byItemKey.has(itemKey)) {
          byItemKey.set(itemKey, { listId, entry });
        }

        itemCardKeys(entry).forEach((cardKey) => {
          if (!byCardKey.has(cardKey)) byCardKey.set(cardKey, { listId, entry });
        });
      }
    }

    stateIndex = { byItemKey, byCardKey };
  }

  function applyPayload(payload) {
    stateCache = normalizePayload(payload);
    localActiveListId = stateCache.activeListId || 'reorder';
    rebuildStateIndex(stateCache);
    return stateCache;
  }

  function currentRevision() {
    return Number(getState().meta?.revision || 0);
  }

  function mutationEnvelope(extra = {}) {
    return { ...extra, expectedRevision: currentRevision() };
  }

  function syncConflictState(error, fallbackNote = 'Workbench changed in another tab. The latest state has been loaded.') {
    if (error?.status !== 409 || !error?.payload?.currentPayload) return false;
    applyPayload(error.payload.currentPayload);
    setAccountNote(error.payload?.message || fallbackNote);
    renderAll();
    return true;
  }

  function getState() {
    return stateCache || fallbackState();
  }

  function defaultListId() {
    const state = getState();
    const requested = clean(localActiveListId || state.activeListId || 'reorder');
    return state.lists.some((list) => list.id === requested) ? requested : 'reorder';
  }

  function listById(listId) {
    return getState().lists.find((list) => list.id === listId) || null;
  }

  function listNameById(listId) {
    return listById(listId)?.name || 'Workbench';
  }

  function listNotesById(listId) {
    return clean(listById(listId)?.notes || '');
  }

  function listDescription(list) {
    if (!list) return 'Custom project list for jobs, rooms, service calls, and project context. Project lists can be renamed or deleted.';
    if (list.system) {
      const descriptions = {
        reorder: 'Products this dealer has purchased before.',
        standards: 'Products this dealer commonly uses, stocks, recommends, or specifies.',
        service: 'Products saved for future review or ordering.',
      };
      return descriptions[list.id] || 'Saved products list for dealer workflows. Saved lists have fixed names and cannot be renamed or deleted.';
    }
    return 'Custom project list for jobs, rooms, service calls, and project context. Project lists can be renamed or deleted.';
  }

  function isCustomProjectList(listId) {
    const list = listById(listId);
    return Boolean(list && list.type === 'project' && list.system === false && list.id !== 'projects');
  }

  function itemKeyLike(obj) {
    return (
      clean(obj?.variantId) ||
      clean(obj?.productId) ||
      clean(obj?.handle) ||
      clean(obj?.sku)
    );
  }

  function cardIdentity(card) {
    return {
      variantId: clean(card.dataset.variantId),
      productId: clean(card.dataset.productId),
      handle: clean(card.dataset.handle),
      sku: clean(card.dataset.sku),
    };
  }

  function entryMatchesCard(entry, card) {
    const identity = cardIdentity(card);
    return (
      (identity.variantId && clean(entry?.variantId) === identity.variantId) ||
      (identity.productId && clean(entry?.productId) === identity.productId) ||
      (identity.handle && clean(entry?.handle) === identity.handle) ||
      (identity.sku && clean(entry?.sku) === identity.sku)
    );
  }

  function getSavedEntryForCard(card) {
    const directKey = itemCardKeys(cardIdentity(card)).find((key) => stateIndex.byCardKey.has(key));
    return directKey ? stateIndex.byCardKey.get(directKey) : null;
  }

  function getCards(root = document) {
    return qa(root, CARD_SELECTOR);
  }

  function currentItems() {
    return getState().itemsByList?.[defaultListId()] || [];
  }

  function selectionSet(listId = defaultListId()) {
    if (!uiState.selections[listId]) uiState.selections[listId] = new Set();
    return uiState.selections[listId];
  }

  function selectedKeys(listId = defaultListId()) {
    return Array.from(selectionSet(listId));
  }

  function visibleRowKeys() {
    return sortedAndFilteredItems(currentItems()).map((item) => itemKeyLike(item)).filter(Boolean);
  }

  function visibleSelectedKeys(listId = defaultListId()) {
    const selected = new Set(selectedKeys(listId));
    return visibleRowKeys().filter((key) => selected.has(key));
  }

  function toggleAllVisible(checked, listId = defaultListId()) {
    const set = selectionSet(listId);
    visibleRowKeys().forEach((key) => {
      if (checked) set.add(key);
      else set.delete(key);
    });
  }

  function clearSelection(listId = defaultListId()) {
    selectionSet(listId).clear();
  }

  function setSelection(listId, key, checked) {
    const set = selectionSet(listId);
    if (checked) set.add(key);
    else set.delete(key);
  }

  function isMobileViewport() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function computeSubtotal(items) {
    return (items || []).reduce((sum, item) => {
      const qty = Math.max(1, Number(item?.qtyHint || 1));
      return sum + (Number(item?.priceCents || 0) * qty);
    }, 0);
  }

  function lineTotalCents(item) {
    const qty = Math.max(1, Number(item?.qtyHint || 1));
    return Number(item?.priceCents || 0) * qty;
  }

  function searchableText(item) {
    return [
      item?.title,
      item?.vendor,
      item?.sku,
      item?.handle,
    ].map(clean).join(' ').toLowerCase();
  }

  function sortedAndFilteredItems(items) {
    const query = clean(uiState.query).toLowerCase();
    let next = [...(items || [])];
    if (query) next = next.filter((item) => searchableText(item).includes(query));

    next.sort((a, b) => {
      if (uiState.sort === 'title') {
        return clean(a?.title).localeCompare(clean(b?.title));
      }
      if (uiState.sort === 'vendor') {
        return clean(a?.vendor).localeCompare(clean(b?.vendor));
      }
      if (uiState.sort === 'price-asc') {
        return Number(a?.priceCents || 0) - Number(b?.priceCents || 0);
      }
      if (uiState.sort === 'price-desc') {
        return Number(b?.priceCents || 0) - Number(a?.priceCents || 0);
      }
      return String(b?.savedAt || '').localeCompare(String(a?.savedAt || ''));
    });

    return next;
  }

  function normalizeSystemListDisplay(list) {
    if (!list || !Object.prototype.hasOwnProperty.call(SYSTEM_LIST_LABELS, list.id)) {
      return list;
    }
    return {
      ...list,
      name: SYSTEM_LIST_LABELS[list.id],
    };
  }

  function getListSummaries() {
    const state = getState();
    if (Array.isArray(state.listSummaries) && state.listSummaries.length) {
      return state.listSummaries
        .map((summary) => normalizeSystemListDisplay({
          ...inferListDetails(summary),
          ...summary,
          count: Number(summary.count || 0),
          isActive: summary.id === defaultListId(),
        }))
        .filter((summary) => summary.id !== 'projects');
    }

    return state.lists.map((list) => normalizeSystemListDisplay({
      ...list,
      count: (state.itemsByList?.[list.id] || []).length,
      isActive: list.id === defaultListId(),
    }));
  }

  function stateStatus() {
    const state = getState();
    if (!state.loggedIn) return 'logged-out';
    if (!state.authorized) return 'approval-pending';
    if (uiState.loading) return 'loading';
    if (uiState.bootstrapError) return 'error';
    return 'ready';
  }

  async function requestJSON(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      const error = new Error(payload?.message || payload?.reason || 'Workbench request failed');
      error.payload = payload;
      error.status = response.status;
      throw error;
    }

    return payload;
  }

  function setAccountNote(message) {
    const root = getRoot();
    if (!root) return;
    setTextAll(root, '[data-sc-workbench-account-note]', message || '');
  }

  function serializeItem(card) {
    return {
      productId: card.dataset.productId || null,
      variantId: card.dataset.variantId || null,
      handle: card.dataset.handle || null,
      sku: card.dataset.sku || null,
      title: card.dataset.title || 'Untitled product',
      vendor: card.dataset.vendor || '',
      priceCents: Number(card.dataset.priceCents || 0),
      url: card.dataset.url || null,
      image: card.dataset.image || null,
      available: card.dataset.available !== 'false',
      badges:
        card.dataset.available === 'false'
          ? [{ type: 'quote-ready' }]
          : [{ type: 'reorder-ready' }],
    };
  }

  function setCardState(card, opts = {}) {
    const button = q(card, BUTTON_SELECTOR);
    const status = q(card, STATUS_SELECTOR);
    if (!button || !status) return;

    if (Object.prototype.hasOwnProperty.call(opts, 'error')) {
      const message = clean(opts.error);
      if (message) {
        card.dataset.scWorkbenchError = message;
      } else {
        delete card.dataset.scWorkbenchError;
      }
    }

    const saved = getSavedEntryForCard(card);
    const isLoading = Boolean(opts.loading);
    const errorMessage = clean(card.dataset.scWorkbenchError);
    const hasError = Boolean(errorMessage) && !saved && !isLoading;

    card.classList.toggle('has-error', hasError);
    button.classList.toggle('is-loading', isLoading);
    button.classList.toggle('is-saved', Boolean(saved));
    button.classList.toggle('is-error', hasError);
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');

    if (!config.loggedIn) {
      delete card.dataset.scWorkbenchError;
      card.classList.remove('has-error');
      button.classList.remove('is-error');
      setButtonLabel(button, buttonLabel(button, 'scWorkbenchLoginLabel', 'Login to save'));
      button.disabled = false;
      button.dataset.saved = 'false';
      status.textContent = statusLabel(status, 'scWorkbenchStatusLogin', 'Dealer Workbench available after login.');
      return;
    }

    if (!config.authorized) {
      delete card.dataset.scWorkbenchError;
      card.classList.remove('has-error');
      button.classList.remove('is-error');
      setButtonLabel(button, buttonLabel(button, 'scWorkbenchPendingLabel', 'Dealer approval required'));
      button.disabled = true;
      button.dataset.saved = 'false';
      status.textContent = statusLabel(status, 'scWorkbenchStatusPending', 'Dealer Workbench becomes available once your dealer account is approved.');
      return;
    }

    if (isLoading) {
      delete card.dataset.scWorkbenchError;
      card.classList.remove('has-error');
      button.classList.remove('is-error');
      setButtonLabel(button, buttonLabel(button, 'scWorkbenchLoadingLabel', 'Saving...'));
      button.disabled = true;
      button.dataset.saved = 'false';
      status.textContent = 'Updating your Workbench.';
      return;
    }

    button.disabled = false;

    if (saved) {
      delete card.dataset.scWorkbenchError;
      card.classList.remove('has-error');
      button.classList.remove('is-error');
      setButtonLabel(button, buttonLabel(button, 'scWorkbenchSavedLabel', 'Saved'));
      button.dataset.saved = 'true';
      status.textContent = `In ${listNameById(saved.listId)}.`;
      return;
    }

    setButtonLabel(button, buttonLabel(button, 'scWorkbenchDefaultLabel', 'Save'));
    button.dataset.saved = 'false';

    if (hasError) {
      status.textContent = errorMessage;
      return;
    }

    status.textContent = statusLabel(status, 'scWorkbenchStatusUnsaved', `Save to ${listNameById(defaultListId())}.`);
  }

  function renderCards(root = document) {
    getCards(root).forEach((card) => setCardState(card));
  }

  async function loadWorkbench(force = false) {
    if (!config.loggedIn) {
      applyPayload(fallbackState());
      uiState.loading = false;
      uiState.bootstrapError = false;
      renderAll();
      return stateCache;
    }

    if (!force && stateCache) return stateCache;
    if (!force && bootstrapPromise) return bootstrapPromise;

    uiState.loading = true;
    uiState.bootstrapError = false;
    renderAll();

    bootstrapPromise = requestJSON(config.proxyBase)
      .then((payload) => {
        applyPayload(payload);
        uiState.bootstrapError = false;
        return stateCache;
      })
      .catch((error) => {
        console.warn('[workbench] bootstrap failed', error);
        applyPayload(fallbackState());
        uiState.bootstrapError = true;
        return stateCache;
      })
      .finally(() => {
        uiState.loading = false;
        bootstrapPromise = null;
        renderAll();
      });

    return bootstrapPromise;
  }

  async function persistActiveList(listId) {
    const payload = await requestJSON(`${config.proxyBase}/list/${encodeURIComponent(listId)}`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ action: 'set_active' })),
    });

    applyPayload(payload);
    localActiveListId = stateCache.activeListId || listId;
    clearSelection(listId);
    uiState.query = '';
    renderAll();
  }

  async function createProjectList(name, makeActive = true) {
    const payload = await requestJSON(`${config.proxyBase}/lists`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ action: 'create', type: 'project', name, makeActive })),
    });

    applyPayload(payload);
    localActiveListId = stateCache.activeListId || defaultListId();
    clearSelection(localActiveListId);
    renderAll();
  }

  async function renameCurrentList(name) {
    const payload = await requestJSON(`${config.proxyBase}/list/${encodeURIComponent(defaultListId())}`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ action: 'rename', name })),
    });

    applyPayload(payload);
    renderAll();
  }

  async function updateCurrentListNotes(notes) {
    const payload = await requestJSON(`${config.proxyBase}/list/${encodeURIComponent(defaultListId())}`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ action: 'update_notes', notes })),
    });

    applyPayload(payload);
    renderAll();
  }

  async function deleteCurrentList() {
    const payload = await requestJSON(`${config.proxyBase}/list/${encodeURIComponent(defaultListId())}`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ action: 'delete', moveItemsTo: 'reorder' })),
    });

    applyPayload(payload);
    localActiveListId = stateCache.activeListId || 'reorder';
    clearSelection();
    renderAll();
  }

  async function moveAccountItems(fromListId, itemKeys, toListId) {
    const payload = await requestJSON(`${config.proxyBase}/move`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ fromListId, toListId, itemKeys })),
    });

    applyPayload(payload);
    localActiveListId = stateCache.activeListId || defaultListId();
    clearSelection(fromListId);
    renderAll();
  }

  async function removeAccountItem(listId, key) {
    const payload = await requestJSON(`${config.proxyBase}/remove`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ listId, key })),
    });

    applyPayload(payload);
    clearSelection(listId);
    renderAll();
  }

  async function removeAccountItems(listId, keys) {
    const payload = await requestJSON(`${config.proxyBase}/remove`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ listId, keys })),
    });

    applyPayload(payload);
    clearSelection(listId);
    renderAll();
  }

  async function updateItemQty(listId, key, qty) {
    const payload = await requestJSON(`${config.proxyBase}/qty`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ listId, key, qty })),
    });

    applyPayload(payload);
    renderAll();
    return payload;
  }

  async function quickAddSkus(rawSkus, qty) {
    const payload = await requestJSON(`${config.proxyBase}/sku`, {
      method: 'POST',
      body: JSON.stringify(mutationEnvelope({ listId: defaultListId(), skus: rawSkus, qty })),
    });

    applyPayload(payload);
    renderAll();
    return payload;
  }

  function cartPath() {
    return config.cartUrl || window.routes?.cart_url || '/cart';
  }

  function observedCartRoute() {
    return `${config.proxyBase}/observe-cart`;
  }

  function formatObservedSubtotal(cents) {
    const parsed = Number(cents);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return (parsed / 100).toFixed(2);
  }

  function observedCartIdFromState(cartState) {
    return clean(cartState?.token) || clean(cartState?.id);
  }

  function buildObservedCartSnapshot(cartState) {
    if (!cartState || typeof cartState !== 'object') return null;

    const currencyCode = clean(cartState.currency) || clean(cartState.presentment_currency);
    const subtotalAmount = formatObservedSubtotal(
      cartState.items_subtotal_price != null ? cartState.items_subtotal_price : cartState.total_price
    );
    const lines = (Array.isArray(cartState.items) ? cartState.items : [])
      .map((item) => {
        const title = clean(item?.product_title) || clean(item?.title);
        const quantity = Number(item?.quantity || 0);
        if (!title || !Number.isFinite(quantity) || quantity <= 0) return null;

        const line = {
          title,
          quantity,
        };

        const sku = clean(item?.sku);
        const variantId = item?.variant_id != null ? clean(item.variant_id) : '';
        if (sku) line.sku = sku;
        if (variantId) line.variantId = variantId;
        return line;
      })
      .filter(Boolean)
      .slice(0, 12);

    if (!currencyCode || subtotalAmount == null) return null;

    return {
      currencyCode,
      subtotalAmount,
      lines,
    };
  }

  function observedCartSignature(cartId, snapshot) {
    if (!cartId || !snapshot) return '';
    const lines = Array.isArray(snapshot.lines) ? snapshot.lines : [];
    return JSON.stringify({
      cartId,
      subtotalAmount: snapshot.subtotalAmount,
      currencyCode: snapshot.currencyCode,
      lines: lines.map((line) => ({
        title: clean(line?.title),
        sku: clean(line?.sku),
        variantId: clean(line?.variantId),
        quantity: Number(line?.quantity || 0),
      })),
    });
  }

  function getStoredObservedCartSignature() {
    try {
      return window.sessionStorage?.getItem('sc-workbench-observed-cart-signature') || '';
    } catch (error) {
      return '';
    }
  }

  function setStoredObservedCartSignature(signature) {
    try {
      if (window.sessionStorage) {
        if (signature) window.sessionStorage.setItem('sc-workbench-observed-cart-signature', signature);
        else window.sessionStorage.removeItem('sc-workbench-observed-cart-signature');
      }
    } catch (error) {
      // best-effort dedupe only
    }
  }

  async function emitObservedCart(cartState) {
    const cartId = observedCartIdFromState(cartState);
    const snapshot = buildObservedCartSnapshot(cartState);
    if (!cartId || !snapshot) return;

    const signature = observedCartSignature(cartId, snapshot);
    if (!signature) return;

    if (signature === lastObservedCartSignature || signature === getStoredObservedCartSignature()) {
      return;
    }

    await requestJSON(observedCartRoute(), {
      method: 'POST',
      body: JSON.stringify({
        cartId,
        snapshot,
      }),
    });

    lastObservedCartSignature = signature;
    setStoredObservedCartSignature(signature);
  }

  function parseHTML(htmlText) {
    return new DOMParser().parseFromString(String(htmlText || ''), 'text/html');
  }

  async function fetchCartState() {
    const response = await fetch(`${cartPath()}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart state ${response.status}`);
    return response.json();
  }

  async function fetchCartSections() {
    const rootPath = window.Shopify?.routes?.root || '/';
    const separator = rootPath.includes('?') ? '&' : '?';
    const response = await fetch(`${rootPath}${separator}sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error(`cart sections ${response.status}`);
    return response.json();
  }

  function setCartDrawerOverlayBinding(drawer) {
    const overlay = drawer?.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.scWorkbenchBound === 'true') return;
    overlay.addEventListener('click', drawer.close.bind(drawer));
    overlay.dataset.scWorkbenchBound = 'true';
  }

  function syncCartDrawerEmptyState(itemCount) {
    const count = Number(itemCount || 0);
    const isEmpty = count === 0;
    const drawer = document.querySelector('cart-drawer');
    const drawerItems = document.querySelector('cart-drawer-items');
    const drawerInner = drawer?.querySelector('.drawer__inner');
    const drawerFooter = drawer?.querySelector('.drawer__footer');
    const emptyWrap = drawer?.querySelector('.drawer__inner-empty');

    if (drawer) drawer.classList.toggle('is-empty', isEmpty);
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);
    if (drawerInner) drawerInner.classList.toggle('is-empty', isEmpty);
    if (drawerFooter) drawerFooter.classList.toggle('is-empty', isEmpty);
    if (emptyWrap) emptyWrap.hidden = !isEmpty;
  }

  function publishCartUpdateEvent(cartState) {
    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sc-workbench',
          cartData: cartState,
          variantId: null,
        });
      }
    } catch (error) {
      console.warn('[workbench] cart publish failed', error);
    }
  }

  async function refreshAndOpenCartDrawer(trigger) {
    const drawer = document.querySelector('cart-drawer');
    const cartIcon = document.querySelector('#cart-icon-bubble');

    let cartState = null;
    let sections = null;

    try {
      cartState = await fetchCartState();
    } catch (error) {
      console.warn('[workbench] cart state refresh failed', error);
    }

    try {
      sections = await fetchCartSections();
    } catch (error) {
      console.warn('[workbench] cart sections refresh failed', error);
    }

    if (cartState) {
      publishCartUpdateEvent(cartState);
    }

    if (drawer && sections?.['cart-drawer']) {
      setCartDrawerOverlayBinding(drawer);
      if (trigger && typeof drawer.setActiveElement === 'function') drawer.setActiveElement(trigger);

      if (typeof drawer.renderContents === 'function') {
        drawer.renderContents({
          id: cartState?.id || Date.now(),
          sections,
        });
        syncCartDrawerEmptyState(cartState?.item_count || 0);
        return cartState;
      }

      try {
        const html = parseHTML(sections['cart-drawer']);
        const source = html.querySelector('#CartDrawer');
        const target = document.querySelector('#CartDrawer');
        if (source && target) target.replaceWith(source);
        if (sections['cart-icon-bubble'] && cartIcon) {
          const bubble = parseHTML(sections['cart-icon-bubble']).querySelector('.shopify-section');
          cartIcon.innerHTML = bubble ? bubble.innerHTML : sections['cart-icon-bubble'];
        }
        syncCartDrawerEmptyState(cartState?.item_count || 0);
        drawer.open(trigger || cartIcon || undefined);
        return cartState;
      } catch (error) {
        console.warn('[workbench] cart drawer replace failed', error);
      }
    }

    if (drawer && typeof drawer.open === 'function') {
      syncCartDrawerEmptyState(cartState?.item_count || 0);
      drawer.open(trigger || cartIcon || undefined);
      return cartState;
    }

    if (cartIcon) {
      cartIcon.click();
      return cartState;
    }

    window.location.assign(cartPath());
    return cartState;
  }

  function wbFlag(value) {
    return value === true || value === 'true';
  }

  function canObserveCart() {
    return (
      wbFlag(config.enabled) &&
      wbFlag(config.loggedIn) &&
      wbFlag(config.authorized) &&
      typeof config.proxyBase === 'string' &&
      clean(config.proxyBase)
    );
  }

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (typeof Request !== 'undefined' && input instanceof Request) return input.url;
    return '';
  }

  function requestMethod(input, init) {
    if (init?.method) return String(init.method).toUpperCase();
    if (typeof Request !== 'undefined' && input instanceof Request && input.method) {
      return String(input.method).toUpperCase();
    }
    return 'GET';
  }

  function isCartMutationUrl(url, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    if (!url) return false;

    let parsed;
    try {
      parsed = new URL(url, window.location.href);
    } catch (error) {
      return false;
    }

    if (parsed.origin !== window.location.origin) return false;

    const pathname = parsed.pathname.replace(/\/+$/, '') || '/';
    return /^(?:\/[a-z]{2}(?:-[a-z]{2})?)?\/cart\/(?:add|change|update|clear)(?:\.(?:js|json))?$/i.test(pathname);
  }

  async function runObservedCartRefresh(reason = 'cart-mutation') {
    if (!canObserveCart()) return;

    if (observedCartObserverInFlight) {
      observedCartObserverPending = true;
      return;
    }

    observedCartObserverInFlight = true;

    try {
      const cartState = await fetchCartState();
      await emitObservedCart(cartState);
    } catch (error) {
      console.warn('[workbench] observed cart refresh failed', error);
    } finally {
      observedCartObserverInFlight = false;

      if (observedCartObserverPending) {
        observedCartObserverPending = false;
        scheduleObservedCartRefresh(reason || 'pending-cart-mutation');
      }
    }
  }

  function scheduleObservedCartRefresh(reason = 'cart-mutation') {
    if (!canObserveCart()) return;

    window.clearTimeout(observedCartObserverTimer);
    observedCartObserverTimer = window.setTimeout(() => {
      runObservedCartRefresh(reason);
    }, 750);
  }

  function installObservedCartFetchObserver() {
    if (window.__scWorkbenchObservedCartFetchObserverInstalled) return;
    if (typeof window.fetch !== 'function') return;

    window.__scWorkbenchObservedCartFetchObserverInstalled = true;

    const originalFetch = window.fetch;
    window.fetch = function scWorkbenchObservedCartFetch(input, init) {
      const url = requestUrl(input);
      const method = requestMethod(input, init);
      const shouldObserve = !suppressNextCartMutationObserve && isCartMutationUrl(url, method);

      const responsePromise = originalFetch.apply(this, arguments);

      if (shouldObserve) {
        responsePromise
          .then((response) => {
            if (response?.ok) {
              scheduleObservedCartRefresh('fetch-cart-mutation');
            }
          })
          .catch(() => {
            // Preserve the storefront's original failed-cart behavior.
          });
      }

      return responsePromise;
    };
  }

  function installObservedCartXhrObserver() {
    if (window.__scWorkbenchObservedCartXhrObserverInstalled) return;
    if (typeof window.XMLHttpRequest !== 'function') return;

    window.__scWorkbenchObservedCartXhrObserverInstalled = true;

    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.XMLHttpRequest.prototype.open = function scWorkbenchObservedCartXhrOpen(method, url) {
      this.__scWorkbenchObservedCartRequest = {
        method: String(method || 'GET').toUpperCase(),
        url: requestUrl(url),
      };

      return originalOpen.apply(this, arguments);
    };

    window.XMLHttpRequest.prototype.send = function scWorkbenchObservedCartXhrSend() {
      const request = this.__scWorkbenchObservedCartRequest;
      const shouldObserve = !suppressNextCartMutationObserve && isCartMutationUrl(request?.url, request?.method);

      if (shouldObserve && this.addEventListener) {
        this.addEventListener('loadend', () => {
          if (this.status >= 200 && this.status < 300) {
            scheduleObservedCartRefresh('xhr-cart-mutation');
          }
        });
      }

      return originalSend.apply(this, arguments);
    };
  }

  function installObservedCartObserver() {
    if (!canObserveCart()) return;

    installObservedCartFetchObserver();
    installObservedCartXhrObserver();
    scheduleObservedCartRefresh('initial-cart-state');
  }

  async function buildAndSubmitCart(listId, itemKeys = []) {
    if (uiState.cartSubmitting) {
      setAccountNote('Cart update already in progress.');
      return;
    }

    uiState.cartSubmitting = true;
    setAccountNote(itemKeys.length ? 'Adding selected items to cart…' : 'Adding active list to cart…');
    renderAll();

    try {
      const payload = await requestJSON(`${config.proxyBase}/cart`, {
        method: 'POST',
        body: JSON.stringify({ listId, itemKeys, mode: itemKeys.length ? 'selected' : 'all' }),
      });

      if (!payload?.lines?.length) {
        setAccountNote('No available items to add to cart.');
        return;
      }

      // Workbench list carts bypass the PDP and native Quick Order. Verify
      // each variant through the existing authenticated SKU resolver and
      // read its Lotus policy from the same storefront product metafields.
      try {
        if (!window.SC_LotusOrderGate) throw new Error('Supplier policy checker unavailable.');
        const itemsForList = getState().itemsByList?.[listId] || [];
        const numericId = (value) => clean(value).split('/').pop();
        const byId = new Map(itemsForList.map((item) => [numericId(item.variantId), item]));
        const candidates = payload.lines.map((line) => {
          const original = byId.get(numericId(line.id));
          const sku = clean(original?.sku || line.sku);
          if (!sku) throw new Error('A saved product is missing its SKU.');
          return { sku, cartVariantId: Number(line.id), quantity: Number(line.quantity || 1) };
        });
        const verified = [];
        for (let index = 0; index < candidates.length; index += 40) {
          const batch = candidates.slice(index, index + 40);
          const response = await requestJSON(config.proxyBase + '/sku/resolve', {
            method: 'POST',
            body: JSON.stringify({ rows: batch.map((line) => ({ sku: line.sku, quantity: line.quantity })) }),
          });
          const bySku = new Map((response.items || []).map((item) => [clean(item.sku).toUpperCase(), item]));
          for (const line of batch) {
            const product = bySku.get(line.sku.toUpperCase());
            if (!product || product.status !== 'resolved' || Number(product.cartVariantId) !== line.cartVariantId) {
              throw new Error('Unable to verify the saved product ' + line.sku + '.');
            }
            verified.push({ sku: line.sku, vendor: product.vendor, handle: product.handle });
          }
        }
        if (!(await window.SC_LotusOrderGate.confirmItems(verified))) {
          setAccountNote('No items added. Review the Lotus supplier terms before retrying.');
          return;
        }
      } catch (error) {
        setAccountNote('Supplier terms could not be verified. No items were added. ' + clean(error?.message));
        return;
      }

      let cartResponse;
      suppressNextCartMutationObserve = true;
      try {
        cartResponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: payload.lines.map((line) => ({
              id: Number(line.id),
              quantity: Number(line.quantity || 1),
            })),
          }),
          credentials: 'same-origin',
        });
      } finally {
        suppressNextCartMutationObserve = false;
      }

      if (!cartResponse.ok) {
        setAccountNote('Could not add items to cart right now.');
        return;
      }

      clearSelection(defaultListId());
      setAccountNote('Refreshing cart…');
      const refreshedCartState = await refreshAndOpenCartDrawer();
      await emitObservedCart(refreshedCartState).catch((error) => {
        console.warn('[workbench] observed cart emit failed', error);
      });

      if (Number(payload?.skippedCount || 0) > 0) {
        setAccountNote(`Added available items to cart. ${payload.skippedCount} item${payload.skippedCount === 1 ? '' : 's'} were skipped.`);
      } else {
        setAccountNote('Added available items to cart.');
      }

      renderAll();
    } finally {
      uiState.cartSubmitting = false;
      renderAll();
    }
  }

  function renderCompatibilityListTabs(root) {
    const legacyMount = q(root, '[data-sc-workbench-list-tabs]');
    if (!legacyMount) return;
    legacyMount.innerHTML = getListSummaries()
      .map((list) => {
        const activeClass = list.id === defaultListId() ? ' is-active' : '';
        const kindClass = list.system ? ' sc-workbench-pill--system' : ' sc-workbench-pill--project';
        return `<button type="button" class="sc-workbench-pill${activeClass}${kindClass}" data-sc-workbench-list-pill="${escapeHtml(list.id)}">${escapeHtml(list.name)} (${Number(list.count || 0)})</button>`;
      })
      .join('');
  }

  function renderRailList(root, roleName, lists) {
    const mount = role(root, roleName);
    if (!mount) return;

    mount.innerHTML = lists.map((list) => {
      const isActive = list.id === defaultListId();
      return `
        <button
          type="button"
          class="wb-rail__item${isActive ? ' is-active' : ''}"
          data-wb-action="switch-list"
          data-wb-list-id="${escapeHtml(list.id)}"
          data-wb-list-type="${escapeHtml(list.type || '')}"
          data-wb-list-system="${list.system ? 'true' : 'false'}"
          data-sc-workbench-list-pill="${escapeHtml(list.id)}"
        >
          <span class="wb-rail__item-copy">
            <span class="wb-rail__item-label">${escapeHtml(list.name)}</span>
            <span class="wb-rail__item-meta">${escapeHtml(list.system ? 'Saved list' : 'Project list')}</span>
          </span>
          <span class="wb-rail__item-count">${Number(list.count || 0)}</span>
        </button>
      `;
    }).join('');
  }

  function renderRail(root) {
    const summaries = getListSummaries();
    renderRailList(root, 'system-lane-list', summaries.filter((list) => list.system));
    renderRailList(root, 'project-list-list', summaries.filter((list) => !list.system));
    renderCompatibilityListTabs(root);
  }

  function syncTextRegions(root) {
    const state = getState();
    const activeList = listById(defaultListId()) || { name: 'Workbench', system: false, type: 'project', notes: '' };
    const items = currentItems();
    const totalItems = Number(state.meta?.pulse?.totalItems || Object.values(state.itemsByList || {}).flat().length || 0);
    const activeCount = Number(items.length);
    const repeatCount = Number((state.itemsByList?.reorder || []).length);
    const subtotal = money(computeSubtotal(items));
    const activeSummary = state.activeListSummary || {
      availableCount: items.filter((item) => item.available !== false).length,
      unavailableCount: items.filter((item) => item.available === false || clean(item?.availabilityState) === 'unavailable').length,
      projectNote: listNotesById(defaultListId()),
      kind: activeList.type || '',
    };
    const kindLabelMap = { reorder: 'Reorder', standard: 'Standard', service: 'Service', project: 'Project' };
    const kindLabel = kindLabelMap[clean(activeSummary.kind || activeList.type)] || 'List';

    setTextAll(root, '[data-sc-workbench-total-items]', String(totalItems));
    setTextAll(root, '[data-sc-workbench-active-count]', String(activeCount));
    setTextAll(root, '[data-sc-workbench-active-list-name]', activeList.name);
    setTextAll(root, '[data-sc-workbench-active-title]', activeList.name);
    setTextAll(root, '[data-sc-workbench-active-subtitle]', listDescription(activeList));
    setTextAll(root, '[data-wb-role="repeat-count"]', String(repeatCount));
    setTextAll(root, '[data-wb-role="subtotal"]', subtotal);
    setTextAll(root, '[data-wb-role="summary-subtotal"]', subtotal);
    setTextAll(root, '[data-wb-role="header-subtotal"]', subtotal);
    setTextAll(root, '[data-wb-role="active-kind"]', kindLabel);
    setTextAll(root, '[data-wb-role="summary-available-count"]', String(Number(activeSummary.availableCount || 0)));
    setTextAll(root, '[data-wb-role="summary-unavailable-count"]', String(Number(activeSummary.unavailableCount || 0)));
    setTextAll(root, '[data-wb-role="summary-project-note"]', clean(activeSummary.projectNote));
    setTextAll(root, '[data-wb-role="header-status-summary"]', `${Number(activeSummary.unavailableCount || 0)} unavailable`);
    setTextAll(root, '[data-wb-role="summary-warning"]', Number(activeSummary.unavailableCount || 0) > 0 ? 'Some items may require follow-up before ordering.' : '');
  }

  function renderToolbar(root) {
    const bulkWrap = role(root, 'bulk-actions');
    const selectedCountNode = role(root, 'selected-count');
    const selected = selectedKeys();
    const visibleKeys = visibleRowKeys();
    const visibleSelected = visibleSelectedKeys();
    const hasSelection = selected.length > 0;
    const addableSelected = currentItems().filter((item) => selected.includes(itemKeyLike(item)) && item.available !== false && clean(item.variantId));
    const toggleAll = q(root, '[data-wb-action="toggle-all"]');
    const clearBtn = q(root, '[data-wb-action="clear-selection"]');

    if (bulkWrap) {
      bulkWrap.dataset.hasSelection = hasSelection ? 'true' : 'false';
      setHidden(bulkWrap, false);
    }

    if (selectedCountNode) selectedCountNode.textContent = hasSelection ? `${selected.length} selected` : 'No items selected';

    const search = q(root, '[data-wb-action="search-list"]');
    if (search && search.value !== uiState.query) search.value = uiState.query;

    const sort = q(root, '[data-wb-action="sort-list"]');
    if (sort && sort.value !== uiState.sort) sort.value = uiState.sort;

    const moveSelectedBtn = q(root, '[data-wb-action="move-selected"]');
    const removeSelectedBtn = q(root, '[data-wb-action="remove-selected"]');
    const addSelectedBtn = q(root, '[data-wb-action="add-selected-to-cart"]');

    if (moveSelectedBtn) moveSelectedBtn.disabled = uiState.cartSubmitting || !hasSelection;
    if (removeSelectedBtn) removeSelectedBtn.disabled = uiState.cartSubmitting || !hasSelection;
    if (addSelectedBtn) addSelectedBtn.disabled = uiState.cartSubmitting || !addableSelected.length;
    if (clearBtn) clearBtn.disabled = uiState.cartSubmitting || !hasSelection;
    syncCartBusyUi(root);
    if (toggleAll) {
      toggleAll.disabled = !visibleKeys.length;
      toggleAll.checked = visibleKeys.length > 0 && visibleSelected.length === visibleKeys.length;
      toggleAll.indeterminate = visibleSelected.length > 0 && visibleSelected.length < visibleKeys.length;
    }
  }

  function renderStates(root) {
    const status = stateStatus();
    if (root) root.dataset.wbState = status;
    const rows = role(root, 'rows');
    const empty = role(root, 'empty');
    const error = role(root, 'error');
    const loading = role(root, 'loading');
    const toolbar = role(root, 'toolbar');
    const summary = role(root, 'summary');
    const mobilebar = role(root, 'mobilebar');

    setHidden(error, status !== 'error');
    setHidden(loading, status !== 'loading');
    setHidden(empty, true);

    if (status === 'logged-out') {
      setTextAll(root, '[data-sc-workbench-account-message]', 'Log in to see dealer pricing and use Dealer Workbench.');
      setAccountNote('');
      if (rows) rows.innerHTML = '';
      setHidden(toolbar, true);
      setHidden(summary, false);
      setHidden(mobilebar, true);
      setDisabledAll(root, '[data-sc-workbench-add-active-list]', true);
      return false;
    }

    if (status === 'approval-pending') {
      setTextAll(root, '[data-sc-workbench-account-message]', 'Dealer Workbench becomes available once your dealer account is approved.');
      setAccountNote('');
      if (rows) rows.innerHTML = '';
      setHidden(toolbar, true);
      setHidden(summary, false);
      setHidden(mobilebar, true);
      setDisabledAll(root, '[data-sc-workbench-add-active-list]', true);
      return false;
    }

    setHidden(toolbar, false);
    return true;
  }

  function renderAttributes(item) {
    const attributes = item && item.attributes && typeof item.attributes === 'object' ? item.attributes : {};
    const entries = Object.entries(attributes).filter(([, value]) => clean(value));
    if (!entries.length) return '';
    return entries.map(([label, value]) => `<span class="wb-row__attribute"><span class="wb-row__attribute-label">${escapeHtml(label)}:</span> ${escapeHtml(value)}</span>`).join('');
  }

  function rowQtyValue(rowNode) {
    const node = q(rowNode, '[data-wb-role="qty-value"]');
    const raw = node && typeof node.value !== 'undefined' ? node.value : node?.textContent;
    return Math.max(1, Number(raw || 1));
  }

  function renderRows(root) {
    const rowsWrap = role(root, 'rows');
    const empty = role(root, 'empty');
    if (!rowsWrap) return;

    const items = sortedAndFilteredItems(currentItems());
    const activeList = listById(defaultListId()) || { id: defaultListId(), name: 'Workbench', system: false };
    const selected = selectionSet(defaultListId());

    if (!items.length) {
      rowsWrap.innerHTML = '';
      setHidden(empty, false);
      const emptyTitle = q(empty, '.wb-empty__title');
      const emptyText = q(empty, '.wb-empty__text');
      if (emptyTitle) emptyTitle.textContent = uiState.query ? 'No matching items' : 'No items yet';
      if (emptyText) {
        emptyText.textContent = uiState.query
          ? 'Try another search term or switch to a different list.'
          : 'Save products while browsing and they will appear here for recurring work, project lists, and future review.';
      }
      return;
    }

    setHidden(empty, true);

    rowsWrap.innerHTML = items.map((item) => {
      const key = itemKeyLike(item);
      const image = clean(item.image);
      const qty = Math.max(1, Number(item.qtyHint || 1));
      const checked = selected.has(key) ? 'checked' : '';
      const subtitle = clean(item.vendor) || 'Dealer product';
      const placeholder = image
        ? `<img class="wb-row__thumb" src="${escapeHtml(image)}" alt="">`
        : '<span class="wb-row__thumb is-placeholder"></span>';
      const availabilityLabelRaw = clean(item.availabilityLabel);
      const availabilityLabel = availabilityLabelRaw && !/unknown/i.test(availabilityLabelRaw)
        ? availabilityLabelRaw
        : (item.available === false ? 'Unavailable' : '');
      const lineTotal = money(lineTotalCents(item));
      const unitPrice = money(item.priceCents || 0);
      const attributes = renderAttributes(item);
      return `
        <article
          class="wb-row${selected.has(key) ? ' is-selected' : ''}"
          data-wb-role="row"
          data-wb-list-id="${escapeHtml(activeList.id)}"
          data-wb-item-key="${escapeHtml(key)}"
          data-wb-item-variant-id="${escapeHtml(clean(item.variantId))}"
        >
          <div class="wb-row__select">
            <input type="checkbox" data-wb-action="toggle-item" ${checked} aria-label="Select ${escapeHtml(item.title || 'Saved product')}">
          </div>
          <a class="wb-row__thumb-link" href="${escapeHtml(item.url || '#')}">${placeholder}</a>
          <div class="wb-row__body">
            <a class="wb-row__title" href="${escapeHtml(item.url || '#')}">${escapeHtml(item.title || 'Saved product')}</a>
            <div class="wb-row__subtext">${escapeHtml(subtitle)}</div>
            ${clean(item.sku) ? `<div class="wb-row__sku">SKU: ${escapeHtml(item.sku)}</div>` : ''}
            ${availabilityLabel ? `<div class="wb-row__status"><span class="wb-chip wb-chip--status">${escapeHtml(availabilityLabel)}</span></div>` : ''}
            ${attributes ? `<div class="wb-row__attributes" data-wb-role="row-attributes">${attributes}</div>` : ''}
          </div>
          <div class="wb-row__commerce">
            <div class="wb-row__pricing">
              <div class="wb-row__unit">
                <span class="wb-row__label">Unit</span>
                <strong class="wb-row__price">${unitPrice}</strong>
              </div>
              <div class="wb-row__line">
                <span class="wb-row__label">Total</span>
                <strong class="wb-row__line-total">${lineTotal}</strong>
              </div>
            </div>
            <div class="wb-row__qty quantity" aria-label="Quantity controls">
              <button type="button" class="quantity__button wb-qty__btn" data-wb-action="qty-minus" ${qty <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">
                <span aria-hidden="true">−</span>
              </button>
              <input class="quantity__input wb-qty__value" data-wb-role="qty-value" type="number" value="${qty}" inputmode="numeric" readonly aria-label="Quantity">
              <button type="button" class="quantity__button wb-qty__btn" data-wb-action="qty-plus" aria-label="Increase quantity">
                <span aria-hidden="true">+</span>
              </button>
            </div>
          </div>
          <div class="wb-row__actions">
            <button type="button" class="wb-row__action wb-row__action--move" data-wb-action="open-move" aria-label="Move item to another list">Move</button>
            <button type="button" class="wb-row__action wb-row__action--remove" data-wb-action="remove-item">Remove</button>
          </div>
        </article>
      `;
    }).join('');
  }

  function renderSummary(root) {
    const activeItems = currentItems();
    const activeSummary = getState().activeListSummary || {
      availableCount: activeItems.filter((item) => item.available !== false).length,
      unavailableCount: activeItems.filter((item) => item.available === false || clean(item?.availabilityState) === 'unavailable').length,
    };
    const addableCount = activeItems.filter((item) => item.available !== false && clean(item.variantId)).length;
    const mobilebar = role(root, 'mobilebar');
    const quickAdd = role(root, 'quickadd');
    const quickAddEnabled = Boolean(getState().authorized);

    setTextAll(root, '[data-wb-role="summary-available-count"]', String(Number(activeSummary.availableCount || 0)));
    setTextAll(root, '[data-wb-role="summary-unavailable-count"]', String(Number(activeSummary.unavailableCount || 0)));
    setTextAll(root, '[data-wb-role="summary-warning"]', Number(activeSummary.unavailableCount || 0) > 0 ? 'Some items may require follow-up before ordering.' : '');

    setDisabledAll(root, '[data-sc-workbench-add-active-list]', uiState.cartSubmitting || !addableCount || !getState().authorized);
    syncCartBusyUi(root);
    setDisabledAll(root, '[data-wb-action="quickadd-skus"], [data-wb-action="quickadd-qty"], [data-wb-action="quickadd-submit"]', !quickAddEnabled);
    if (quickAdd) {
      quickAdd.classList.toggle('wb-quickadd--disabled', !quickAddEnabled);
      quickAdd.setAttribute('aria-disabled', quickAddEnabled ? 'false' : 'true');
    }
    if (mobilebar) {
      setHidden(mobilebar, !isMobileViewport() || !addableCount || !getState().authorized);
    }
  }

  function setQuickAddMessage(message, tone = '') {
    const root = getRoot();
    if (!root) return;
    const note = role(root, 'quickadd-note');
    if (!note) return;

    note.textContent = message || '';
    note.dataset.tone = tone || '';
    note.classList.toggle('is-success', tone === 'success');
    note.classList.toggle('is-error', tone === 'error');
    note.classList.toggle('is-loading', tone === 'loading');
  }

  function renderListActions(root) {
    const renameToggleNodes = qa(root, '[data-sc-workbench-rename-toggle]');
    const deleteNodes = qa(root, '[data-sc-workbench-delete-list]');
    const showListActions = isCustomProjectList(defaultListId());

    renameToggleNodes.forEach((node) => {
      setHidden(node, !showListActions);
    });

    deleteNodes.forEach((node) => {
      setHidden(node, !showListActions);
    });
  }

  function renderProjectNotes(root) {
    const wrap = q(root, '[data-sc-workbench-project-notes]');
    const input = q(root, '[data-sc-workbench-project-notes-input]');
    const save = q(root, '[data-sc-workbench-project-notes-save]');
    const summaryNoteCard = q(root, '.wb-summary__notes');
    const enabled = isCustomProjectList(defaultListId());
    const noteValue = listNotesById(defaultListId());

    if (wrap) setHidden(wrap, !enabled);
    if (summaryNoteCard) setHidden(summaryNoteCard, !enabled && !clean(noteValue));
    if (!enabled) return;
    if (input && document.activeElement !== input) input.value = noteValue;
    if (save) save.disabled = false;
  }

  function renderMessages(root) {
    const status = stateStatus();
    if (status === 'ready') {
      setTextAll(
        root,
        '[data-sc-workbench-account-message]',
        'Your saved products are organized by buying intent. Save while browsing, then return here to build carts quickly.'
      );
      const activeList = listById(defaultListId());
      const note = currentItems().length
        ? `Working in ${activeList?.name || 'Workbench'}.`
        : `No items in ${activeList?.name || 'Workbench'} yet.`;
      setAccountNote(note);
      setQuickAddMessage(
        getState().authorized
          ? 'Enter one or more SKUs and click Add. Exact matches are added into the active list and any missing SKUs will be shown here.'
          : 'SKU entry becomes available once your dealer account is approved.'
      );
      return;
    }

    if (status === 'loading') {
      setTextAll(root, '[data-sc-workbench-account-message]', 'Loading your saved products…');
      setAccountNote('');
      return;
    }

    if (status === 'error') {
      setTextAll(root, '[data-sc-workbench-account-message]', 'Workbench could not load right now.');
      setAccountNote('You can retry the load or continue browsing.');
    }
  }

  function renderMoveSurface(root) {
    const overlay = getMoveOverlayNode();
    const popover = overlay?.querySelector('[data-wb-role="move-popover"]');
    const sheet = overlay?.querySelector('[data-wb-role="move-sheet"]');
    const context = uiState.moveContext;

    if (!overlay || !popover || !sheet) return;

    if (!context) {
      setHidden(overlay, true);
      overlay.classList.remove('is-open');
      document.body.classList.remove('sc-workbench-overlay-open');
      setHidden(popover, true);
      setHidden(sheet, true);
      popover.innerHTML = '';
      sheet.innerHTML = '';
      return;
    }

    const fromListId = context.fromListId || defaultListId();
    const fromListName = listNameById(fromListId);
    const moveCount = Math.max(1, Number((context.itemKeys || []).length || 1));
    const itemLabel = moveCount === 1 ? 'item' : 'items';
    const dialogLabel = context.mode === 'single' ? 'Move item' : 'Move selected items';
    const systemLists = getListSummaries().filter((list) => list.system);
    const projectLists = getListSummaries().filter((list) => !list.system);

    const renderOptions = (lists, emptyMessage) => {
      if (!lists.length) {
        return `<p class="wb-move__empty">${escapeHtml(emptyMessage)}</p>`;
      }

      return `
        <div class="wb-move__grid">
          ${lists.map((list) => {
            const isCurrent = list.id === fromListId;
            const count = Number(list.count || 0);
            const countLabel = `${count} saved ${count === 1 ? 'item' : 'items'}`;
            return `
              <button
                type="button"
                class="wb-move__option${isCurrent ? ' is-current' : ''}"
                data-wb-action="move-to-destination"
                data-wb-target-list-id="${escapeHtml(list.id)}"
                ${isCurrent ? 'disabled' : ''}
              >
                <span class="wb-move__option-main">
                  <span class="wb-move__option-title">${escapeHtml(list.name)}</span>
                  <span class="wb-move__option-meta">${escapeHtml(list.system ? 'Saved list' : 'Project list')} • ${escapeHtml(countLabel)}</span>
                </span>
                <span class="wb-move__option-pill">${isCurrent ? 'Current list' : 'Move here'}</span>
              </button>
            `;
          }).join('')}
        </div>
      `;
    };

    const content = `
      <div class="wb-move" role="dialog" aria-modal="true" aria-label="${escapeHtml(dialogLabel)}">
        <div class="wb-move__header">
          <div class="wb-move__header-copy">
            <p class="wb-move__eyebrow">${escapeHtml(dialogLabel)}</p>
            <h3 class="wb-move__title">Choose a destination list</h3>
            <p class="wb-move__description">Move ${moveCount} ${itemLabel} from ${escapeHtml(fromListName)} without leaving the workspace.</p>
          </div>
          <button type="button" class="button button--secondary wb-move__close" data-wb-action="close-move">Close</button>
        </div>
        <div class="wb-move__section">
          <p class="wb-move__label">Saved Products</p>
          ${renderOptions(systemLists, 'No saved product lists available right now.')}
        </div>
        <div class="wb-move__section">
          <div class="wb-move__section-head">
            <p class="wb-move__label">Project lists</p>
            <button type="button" class="button button--secondary wb-move__new-list" data-sc-workbench-create-toggle data-wb-action="new-project-list">+ New project list</button>
          </div>
          ${renderOptions(projectLists, 'No project lists yet. Create one and move these items into it.')}
        </div>
      </div>
    `;

    setHidden(overlay, false);
    overlay.classList.add('is-open');
    document.body.classList.add('sc-workbench-overlay-open');

    if (isMobileViewport()) {
      sheet.innerHTML = content;
      setHidden(sheet, false);
      setHidden(popover, true);
    } else {
      popover.innerHTML = content;
      setHidden(popover, false);
      setHidden(sheet, true);
    }
  }

  function renderAccount() {
    const root = getRoot();
    if (!root) return;

    const statusReady = renderStates(root);
    syncTextRegions(root);
    renderMessages(root);
    renderRail(root);
    renderListActions(root);
    renderProjectNotes(root);
    renderToolbar(root);

    if (statusReady) {
      renderRows(root);
      renderSummary(root);
    }

    renderMoveSurface(root);

    try {
      const detail = {
        state: getState(),
        activeListId: defaultListId(),
        currentItems: currentItems(),
        summaries: getListSummaries(),
        selectedKeys: selectedKeys(),
      };
      root.dataset.wbActiveListId = detail.activeListId || '';
      window.SC_WORKBENCH_LAST_RENDER = detail;
      root.dispatchEvent(new CustomEvent('sc:workbench:render', { bubbles: true, detail }));
    } catch (error) {
      console.warn('[workbench] render event failed', error);
    }
  }

  function renderAll(root = document) {
    renderCards(root);
    renderAccount();
  }

  function redirectToLogin() {
    const loginUrl = config.loginUrl || '/account/login';
    const returnTo = config.returnTo || window.location.pathname;
    window.location.assign(`${loginUrl}?return_url=${encodeURIComponent(returnTo)}`);
  }

  function openMoveContext(context) {
    uiState.moveContext = context;
    renderAccount();
    requestAnimationFrame(() => focusMoveSurface());
  }

  function closeMoveContext() {
    uiState.moveContext = null;
    renderAccount();
  }

  function imageUrlFromVariant(variant) {
    return clean(variant?.featured_image?.src) ||
      clean(variant?.featured_media?.preview_image?.src) ||
      clean(variant?.image?.src) ||
      '';
  }

  function syncProductContextCard(card, variant) {
    if (!card || !variant) return;

    if (variant.id != null) card.dataset.variantId = String(variant.id);
    card.dataset.sku = clean(variant.sku);
    if (variant.price != null) card.dataset.priceCents = String(variant.price);
    card.dataset.available = variant.available === false ? 'false' : 'true';

    const variantUrl = clean(variant.url);
    if (variantUrl) card.dataset.url = variantUrl;

    const imageUrl = imageUrlFromVariant(variant);
    if (imageUrl) card.dataset.image = imageUrl;

    setCardState(card);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const sectionId = clean(event?.data?.sectionId);
      const variant = event?.data?.variant;
      if (!sectionId || !variant) return;
      qa(document, `[data-sc-workbench-card][data-sc-workbench-context="product"][data-sc-workbench-section-id="${sectionId}"]`).forEach((card) => {
        syncProductContextCard(card, variant);
      });
    });
  }

  document.addEventListener('click', async (event) => {
    const saveButton = event.target.closest(BUTTON_SELECTOR);
    if (saveButton) {
      const card = saveButton.closest(CARD_SELECTOR) || saveButton.parentElement?.closest(CARD_SELECTOR);
      if (!card) return;
      event.preventDefault();
      event.stopPropagation();

      if (!config.loggedIn) {
        redirectToLogin();
        return;
      }
      if (!config.authorized) return;

      if (saveButton.dataset.saved === 'true') {
        if (config.accountUrl) window.location.assign(`${config.accountUrl}#dealer-workbench`);
        return;
      }

      await loadWorkbench();
      setCardState(card, { error: '' });
      setCardState(card, { loading: true });

      try {
        const payload = await requestJSON(`${config.proxyBase}/save`, {
          method: 'POST',
          body: JSON.stringify({
            mode: 'upsert',
            listId: defaultListId(),
            item: serializeItem(card),
            expectedRevision: currentRevision(),
          }),
        });
        applyPayload(payload);
        renderAll();
        showToast(`Saved to ${listNameById(defaultListId())}.`, 'success');
      } catch (error) {
        console.warn('[workbench] save failed', error);
        if (!syncConflictState(error)) {
          const message = clean(error?.payload?.message || error?.message) || 'Could not save this product right now.';
          setCardState(card, { error: message });
          showToast(message, 'error');
        }
      }
      return;
    }

    const listSwitch = event.target.closest('[data-wb-action="switch-list"], [data-sc-workbench-list-pill]');
    if (listSwitch) {
      event.preventDefault();
      const listId = listSwitch.dataset.wbListId || listSwitch.dataset.scWorkbenchListPill;
      if (!listId || listId === defaultListId()) return;
      try {
        await persistActiveList(listId);
      } catch (error) {
        console.warn('[workbench] set active failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not switch active list right now.');
      }
      return;
    }

    const createToggle = event.target.closest('[data-sc-workbench-create-toggle], [data-wb-action="new-project-list"]');
    if (createToggle) {
      const root = getRoot();
      const form = q(root, '[data-sc-workbench-create-form]');
      if (form) {
        form.hidden = !form.hidden;
        if (!form.hidden) q(form, '[data-sc-workbench-create-input]')?.focus();
      }
      closeMoveContext();
      return;
    }

    const renameToggle = event.target.closest('[data-sc-workbench-rename-toggle], [data-wb-action="rename-list"]');
    if (renameToggle) {
      const root = getRoot();
      const form = q(root, '[data-sc-workbench-rename-form]');
      if (form) {
        form.hidden = !form.hidden;
        if (!form.hidden) {
          const input = q(form, '[data-sc-workbench-rename-input]');
          if (input) {
            input.value = listNameById(defaultListId());
            input.focus();
          }
        }
      }
      return;
    }

    const createSubmit = event.target.closest('[data-sc-workbench-create-submit], [data-wb-action="create-project-list"]');
    if (createSubmit) {
      const root = getRoot();
      const input = q(root, '[data-sc-workbench-create-input]');
      const name = clean(input?.value);
      if (!name) return;

      createSubmit.disabled = true;
      try {
        await createProjectList(name, true);
        if (input) input.value = '';
        const form = q(root, '[data-sc-workbench-create-form]');
        if (form) form.hidden = true;
        setAccountNote(`Created project list ${name}.`);
      } catch (error) {
        console.warn('[workbench] create list failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not create that project list right now.');
      } finally {
        createSubmit.disabled = false;
      }
      return;
    }

    const renameSubmit = event.target.closest('[data-sc-workbench-rename-submit], [data-wb-action="rename-list-submit"]');
    if (renameSubmit) {
      const root = getRoot();
      const input = q(root, '[data-sc-workbench-rename-input]');
      const name = clean(input?.value);
      if (!name) return;

      renameSubmit.disabled = true;
      try {
        await renameCurrentList(name);
        const form = q(root, '[data-sc-workbench-rename-form]');
        if (form) form.hidden = true;
        setAccountNote(`Renamed list to ${name}.`);
      } catch (error) {
        console.warn('[workbench] rename failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not rename that list right now.');
      } finally {
        renameSubmit.disabled = false;
      }
      return;
    }

    const notesSaveBtn = event.target.closest('[data-sc-workbench-project-notes-save], [data-wb-action="save-project-notes"]');
    if (notesSaveBtn) {
      event.preventDefault();
      const root = getRoot();
      const input = q(root, '[data-sc-workbench-project-notes-input]');
      const notes = input ? input.value : '';
      notesSaveBtn.disabled = true;
      try {
        await updateCurrentListNotes(notes);
        setAccountNote(`Notes saved for ${listNameById(defaultListId())}.`);
      } catch (error) {
        console.warn('[workbench] save notes failed', error);
        if (!syncConflictState(error)) {
          setAccountNote(error?.message || 'Could not save project notes right now.');
        }
      } finally {
        notesSaveBtn.disabled = false;
      }
      return;
    }

    const deleteListBtn = event.target.closest('[data-sc-workbench-delete-list], [data-wb-action="delete-list"]');
    if (deleteListBtn) {
      event.preventDefault();
      const name = listNameById(defaultListId());
      if (!window.confirm(`Delete ${name}? Any items in it will be moved to Buy Again.`)) return;

      try {
        await deleteCurrentList();
        setAccountNote(`Deleted ${name}.`);
      } catch (error) {
        console.warn('[workbench] delete list failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not delete that list right now.');
      }
      return;
    }

    const row = event.target.closest('[data-wb-role="row"]');

    const toggleAllBtn = event.target.closest('[data-wb-action="clear-selection"]');
    if (toggleAllBtn) {
      event.preventDefault();
      clearSelection(defaultListId());
      renderAccount();
      return;
    }

    const minusBtn = event.target.closest('[data-wb-action="qty-minus"]');
    if (minusBtn) {
      event.preventDefault();
      const rowNode = minusBtn.closest('[data-wb-role="row"]');
      if (!rowNode) return;
      const currentQty = rowQtyValue(rowNode);
      const nextQty = Math.max(1, currentQty - 1);
      minusBtn.disabled = true;
      try {
        await updateItemQty(rowNode.dataset.wbListId || defaultListId(), rowNode.dataset.wbItemKey, nextQty);
        setAccountNote('Quantity updated.');
      } catch (error) {
        console.warn('[workbench] qty update failed', error);
        if (!syncConflictState(error)) setAccountNote(error?.message || 'Could not update quantity right now.');
      } finally {
        minusBtn.disabled = false;
      }
      return;
    }

    const plusBtn = event.target.closest('[data-wb-action="qty-plus"]');
    if (plusBtn) {
      event.preventDefault();
      const rowNode = plusBtn.closest('[data-wb-role="row"]');
      if (!rowNode) return;
      const currentQty = rowQtyValue(rowNode);
      plusBtn.disabled = true;
      try {
        await updateItemQty(rowNode.dataset.wbListId || defaultListId(), rowNode.dataset.wbItemKey, currentQty + 1);
        setAccountNote('Quantity updated.');
      } catch (error) {
        console.warn('[workbench] qty update failed', error);
        if (!syncConflictState(error)) setAccountNote(error?.message || 'Could not update quantity right now.');
      } finally {
        plusBtn.disabled = false;
      }
      return;
    }

    const removeBtn = event.target.closest('[data-wb-action="remove-item"], [data-sc-workbench-remove]');
    if (removeBtn) {
      event.preventDefault();
      const rowNode = row || removeBtn.closest('[data-wb-role="row"]');
      const listId = rowNode?.dataset.wbListId || defaultListId();
      const key = rowNode?.dataset.wbItemKey;
      if (!listId || !key) return;

      try {
        await removeAccountItem(listId, key);
        setAccountNote('Removed item from the current list.');
      } catch (error) {
        console.warn('[workbench] remove failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not remove that item right now.');
      }
      return;
    }

    const openMoveBtn = event.target.closest('[data-wb-action="open-move"]');
    if (openMoveBtn) {
      event.preventDefault();
      const rowNode = openMoveBtn.closest('[data-wb-role="row"]');
      if (!rowNode) return;
      openMoveContext({
        mode: 'single',
        fromListId: rowNode.dataset.wbListId || defaultListId(),
        itemKeys: [rowNode.dataset.wbItemKey],
      });
      return;
    }

    const moveSelectedBtn = event.target.closest('[data-wb-action="move-selected"]');
    if (moveSelectedBtn) {
      event.preventDefault();
      const keys = selectedKeys();
      if (!keys.length) return;
      openMoveContext({
        mode: 'bulk',
        fromListId: defaultListId(),
        itemKeys: keys,
      });
      return;
    }

    const moveTargetBtn = event.target.closest('[data-wb-action="move-to-destination"]');
    if (moveTargetBtn) {
      event.preventDefault();
      const targetListId = moveTargetBtn.dataset.wbTargetListId;
      const context = uiState.moveContext;
      if (!context || !targetListId || targetListId === context.fromListId) return;

      moveTargetBtn.disabled = true;
      try {
        await moveAccountItems(context.fromListId, context.itemKeys || [], targetListId);
        setAccountNote(`Moved ${context.itemKeys?.length || 0} item${context.itemKeys?.length === 1 ? '' : 's'} to ${listNameById(targetListId)}.`);
        closeMoveContext();
      } catch (error) {
        console.warn('[workbench] move failed', error);
        if (!syncConflictState(error)) setAccountNote('Could not move that item right now.');
        moveTargetBtn.disabled = false;
      }
      return;
    }

    const addActiveBtn = event.target.closest('[data-sc-workbench-add-active-list], [data-wb-action="add-active-to-cart"]');
    if (addActiveBtn) {
      event.preventDefault();
      try {
        await buildAndSubmitCart(defaultListId());
      } catch (error) {
        console.warn('[workbench] add active list to cart failed', error);
        setAccountNote('Could not add the active list to cart right now.');
      }
      return;
    }

    const addSelectedBtn = event.target.closest('[data-wb-action="add-selected-to-cart"]');
    if (addSelectedBtn) {
      event.preventDefault();
      const keys = selectedKeys();
      if (!keys.length) return;
      try {
        await buildAndSubmitCart(defaultListId(), keys);
      } catch (error) {
        console.warn('[workbench] add selected failed', error);
        setAccountNote('Could not add the selected items to cart right now.');
      }
      return;
    }

    const removeSelectedBtn = event.target.closest('[data-wb-action="remove-selected"]');
    if (removeSelectedBtn) {
      event.preventDefault();
      const keys = selectedKeys();
      if (!keys.length) return;
      if (!window.confirm(`Remove ${keys.length} selected item${keys.length === 1 ? '' : 's'} from this list?`)) return;

      removeSelectedBtn.disabled = true;
      try {
        await removeAccountItems(defaultListId(), keys);
        setAccountNote(`Removed ${keys.length} selected item${keys.length === 1 ? '' : 's'}.`);
      } catch (error) {
        console.warn('[workbench] bulk remove failed', error);
        if (!syncConflictState(error)) {
          setAccountNote('Could not remove the selected items right now.');
        }
      } finally {
        removeSelectedBtn.disabled = false;
      }
      return;
    }

    const quickAddBtn = event.target.closest('[data-wb-action="quickadd-submit"]');
    if (quickAddBtn) {
      event.preventDefault();
      const root = getRoot();
      const skuInput = q(root, '[data-wb-action="quickadd-skus"]');
      const qtyInput = q(root, '[data-wb-action="quickadd-qty"]');
      const rawSkus = skuInput?.value || '';
      const qty = Math.max(1, Number(qtyInput?.value || 1));
      if (!clean(rawSkus)) {
        setQuickAddMessage('Enter one or more SKUs to add to the active list.', 'error');
        setAccountNote('Enter one or more SKUs to add to the active list.');
        return;
      }

      quickAddBtn.disabled = true;
      setQuickAddMessage('Checking SKUs and updating the active list…', 'loading');
      try {
        const payload = await quickAddSkus(rawSkus, qty);
        if (skuInput) skuInput.value = '';
        const added = Array.isArray(payload?.addedSkus) ? payload.addedSkus.length : 0;
        const missing = Array.isArray(payload?.missingSkus) ? payload.missingSkus : [];
        if (added && missing.length) {
          const message = `Added ${added} SKU${added === 1 ? '' : 's'} to ${listNameById(defaultListId())}. Missing: ${missing.join(', ')}.`;
          setQuickAddMessage(message, 'success');
          setAccountNote(message);
        } else if (added) {
          const message = `Added ${added} SKU${added === 1 ? '' : 's'} to ${listNameById(defaultListId())}.`;
          setQuickAddMessage(message, 'success');
          setAccountNote(message);
        } else {
          const message = missing.length
            ? `No SKU matches found. Missing: ${missing.join(', ')}.`
            : 'No SKU matches found for the values entered.';
          setQuickAddMessage(message, 'error');
          setAccountNote(message);
        }
      } catch (error) {
        console.warn('[workbench] quick add failed', error);
        if (!syncConflictState(error)) {
          const message = 'Could not add those SKUs right now.';
          setQuickAddMessage(message, 'error');
          setAccountNote(message);
        }
      } finally {
        quickAddBtn.disabled = false;
      }
      return;
    }

    const retryBootstrapBtn = event.target.closest('[data-wb-action="retry-bootstrap"]');
    if (retryBootstrapBtn) {
      event.preventDefault();
      loadWorkbench(true);
      return;
    }

    const closeMoveBtn = event.target.closest('[data-wb-action="close-move"]');
    if (closeMoveBtn) {
      event.preventDefault();
      closeMoveContext();
    }
  });

  document.addEventListener('change', (event) => {
    const toggleAll = event.target.closest('[data-wb-action="toggle-all"]');
    if (toggleAll) {
      toggleAllVisible(toggleAll.checked, defaultListId());
      renderAccount();
      return;
    }

    const checkbox = event.target.closest('[data-wb-action="toggle-item"]');
    if (checkbox) {
      const row = checkbox.closest('[data-wb-role="row"]');
      if (!row) return;
      setSelection(row.dataset.wbListId || defaultListId(), row.dataset.wbItemKey, checkbox.checked);
      renderAccount();
      return;
    }

    if (event.target.matches('[data-wb-action="sort-list"]')) {
      uiState.sort = clean(event.target.value) || 'recent';
      renderAccount();
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target.matches('[data-wb-action="search-list"]')) {
      uiState.query = event.target.value || '';
      renderAccount();
      return;
    }

    if (event.target.matches('[data-wb-action="quickadd-skus"], [data-wb-action="quickadd-qty"]')) {
      if (getState().authorized) {
        setQuickAddMessage('Enter one or more SKUs and click Add. Exact matches are added into the active list and any missing SKUs will be shown here.');
      }
      return;
    }

    if (event.target.matches('[data-sc-workbench-create-input]') && event.target.value) {
      // no-op, reserved for future validation
      return;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && uiState.moveContext) {
      closeMoveContext();
      return;
    }

    if (event.key !== 'Enter') return;

    if (event.target.matches('[data-sc-workbench-create-input]')) {
      event.preventDefault();
      q(document, '[data-sc-workbench-create-submit]')?.click();
    }

    if (event.target.matches('[data-sc-workbench-rename-input]')) {
      event.preventDefault();
      q(document, '[data-sc-workbench-rename-submit]')?.click();
    }

    if (event.target.matches('[data-wb-action="quickadd-skus"], [data-wb-action="quickadd-qty"]')) {
      const isTextarea = event.target.tagName === 'TEXTAREA';
      if (isTextarea && !(event.metaKey || event.ctrlKey)) {
        return;
      }
      event.preventDefault();
      q(document, '[data-wb-action="quickadd-submit"]')?.click();
    }
  });

  let resizeRaf = null;
  window.addEventListener('resize', () => {
    if (!getRoot()) return;
    if (resizeRaf) cancelAnimationFrame(resizeRaf);
    resizeRaf = requestAnimationFrame(() => {
      resizeRaf = null;
      renderAccount();
    });
  });

  ['shopify:section:load', 'shopify:section:select', 'shopify:block:select'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      renderAll(event.target || document);
    });
  });

  if (document.body) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (
            node.nodeType === 1 &&
            (node.matches?.(CARD_SELECTOR) || node.querySelector?.(CARD_SELECTOR))
          ) {
            renderCards(document);
            return;
          }
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  async function addSkuRowsFromQuickOrder(rows = []) {
    const requestedRows = Array.isArray(rows) ? rows : [];
    const addedSkus = [];
    const missingSkus = [];
    let lastPayload = null;

    for (const row of requestedRows) {
      const sku = clean(row?.sku);
      const qty = Math.max(1, Math.min(99, Number(row?.quantity || row?.qty || 1)));
      if (!sku) continue;
      const payload = await quickAddSkus(sku, qty);
      lastPayload = payload;
      if (Array.isArray(payload?.addedSkus)) addedSkus.push(...payload.addedSkus);
      if (Array.isArray(payload?.missingSkus)) missingSkus.push(...payload.missingSkus);
    }

    const dedupe = (values) => Array.from(new Set(values.map((value) => clean(value)).filter(Boolean)));
    return {
      payload: lastPayload,
      addedSkus: dedupe(addedSkus),
      missingSkus: dedupe(missingSkus),
      activeListId: defaultListId(),
      activeListName: listNameById(defaultListId()),
    };
  }

  window.SC_WORKBENCH_UI = {
    ...(window.SC_WORKBENCH_UI || {}),
    addSkuRows: addSkuRowsFromQuickOrder,
    getActiveListId: () => defaultListId(),
    getState: () => getState(),
  };

  installObservedCartObserver();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      loadWorkbench();
    }, { once: true });
  } else {
    loadWorkbench();
  }
})();
